import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useMessage} from '../../../../hooks/message';
import {useQuestion} from '../../../../hooks/question';
import {useRPC} from '../../../../hooks/rpc';
import {VirtualControlModel} from '../../../../store/control/state';
import {SelectItem} from '../../../../components/Select';
import {BridgeDeviceModel} from '../../../../types/bridge-device';
import {BRIDGE_DEVICE_TYPE_ID_STS_PLUS, CONTROL_TYPE_ID_THERMOSTAT, ControlModel} from '../../../../types/control';
import {useMapSelector} from '../../../../hooks/map-selector';
import {useMap} from '../../../../hooks/map';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {SettingReadOnlyRowComponent} from '../../../../components/Settings/SettingReadOnlyRowComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useGlassControl} from '../../../../hooks/glass-control';
import {SettingCheckBoxComponent} from '../../../../components/Settings/SettingCheckboxComponent';

const BACK_URL = '/settings/virtual-control';

export const VirtualControlEditScreen = memo(function VirtualControlEditScreen() {
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const question = useQuestion();
    const [rpc] = useRPC();
    const history = useHistory();
    const location = useLocation();

    const controlSet = useSetSelector(state => state.control.controlList);
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList);
    const virtualControlSet = useSetSelector(state => state.control.virtualControlList);
    const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMapSelector(
        state => state.control.bridgeDeviceList,
        bridgeDevice => ({
            value: bridgeDevice,
            label: bridgeDevice.name,
        }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => location.state || virtualControlSet[params?.id] || null, []);
    const slot = !location.state ? ((selected?.slot?.toString()) || null) : null;
    const [bridgeDevice, setBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? null :
            bridgeDeviceOptionList.find(option => option.value.id === selected.bridge_device_id) || null),
    );

    const controlBridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMap(useFilterSelector(
        state => state.control.bridgeDeviceList,
        bridgeDeviceItem => bridgeDeviceItem.gateway_id === bridgeDevice?.value?.gateway_id && bridgeDeviceItem.id !== bridgeDevice?.value?.id,
        [bridgeDevice?.value?.id],
    ), bridgeDevice => ({value: bridgeDevice, label: bridgeDevice.name}));
    const [controlBridgeDevice, setControlBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? null :
            controlBridgeDeviceOptionList.find(option => option.value.id === controlSet[selected.control_id]?.bridge_device_id)
            || null),
    );

    const controlOptionList = useMap(useFilterSelector(
        state => state.control.controlList,
        control => control.type_id !== CONTROL_TYPE_ID_THERMOSTAT &&
            controlBridgeDevice?.value?.id === control.bridge_device_id &&
            controlTypeSet[control.type_id].allowed_bridge_device_type_id_list.indexOf(bridgeDevice?.value?.type_id) !== -1,
        [selected?.bridge_device_id, controlBridgeDevice?.value?.id, controlTypeSet],
    ), control => ({
        value: control,
        label: control.name,
    }));

    const [control, setControl] = useState<SelectItem<ControlModel>>(
        (selected == null ? null :
            controlOptionList.find(option => option.value.id === selected.control_id) || null),
    );
    const [navigateToSTPEditor, setNavigateToSTPEditor] = useState<boolean>();

    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
    const bridgeDeviceType = useMemo(() => bridgeDevice == null ? null : bridgeDeviceTypeSet[bridgeDevice.value.type_id],
        [bridgeDevice, bridgeDeviceTypeSet]);

    const {buttonNumber, buttonNumberOptionList, setButtonNumber} =
        useGlassControl(selected, bridgeDevice?.value?.id || null, bridgeDeviceType, control?.value?.type_id);

    const setNavigateStpEditorCheckbox = useCallback(() => {
        setNavigateToSTPEditor(!navigateToSTPEditor);
    }, [navigateToSTPEditor]);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const goToStpEditorWithState = useCallback((state: unknown) => {
        history.push({pathname: `/settings/stp-editor/${bridgeDevice.value.id}/content/`, state});
    }, [bridgeDevice, history]);

    const onDeletePressed = useCallback(() => {
        question('Sanal Kontrol silmek istediğinizden emin misiniz?', () => {
            return rpc.deleteVirtualControl(selected.id)
                .then(() => {
                    success('Sanal Kontrol başarılı bir şekilde silindi.');
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [success, failure, onBackPressed, question, rpc, selected]);

    const isFieldsValid = useCallback((): boolean => {
        if (bridgeDevice == null) {
            failure('Lütfen geçerli bir cihaz seçiniz');

            return false;
        }

        if (control == null) {
            failure('Lütfen geçerli bir kontrol seçiniz');

            return false;
        }

        if (bridgeDeviceType?.button_count > 0) {
            if (buttonNumber?.value == null) {
                failure('Lütfen buton numarası seçiniz.');

                return false;
            }
        }

        return true;
    }, [bridgeDevice, control, failure, bridgeDeviceType, buttonNumber]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const virtualControl: VirtualControlModel = {
            id: selected?.id || null,
            slot: location.state ? -1 : (selected ? selected?.slot : -1),
            bridge_device_id: bridgeDevice.value.id,
            control_id: control.value.id,
            parameters: bridgeDeviceType.is_io_device ? null : {
                button_number: buttonNumber?.value,
            },
        };

        return rpc.updateVirtualControl(virtualControl)
            .then(id => {
                if (navigateToSTPEditor) {
                    goToStpEditorWithState({
                        controlId: id,
                        bridgeDeviceId: bridgeDevice.value.id,
                    });
                } else {
                    success('Sanal kontrol başarılı bir şekilde ' + (selected != null ? 'düzenlendi.' : 'kaydedildi.'));
                    onBackPressed();
                }
            }).catch(failure);
    }, [
        isFieldsValid,
        location.state,
        selected,
        rpc,
        bridgeDevice,
        control,
        success,
        failure,
        onBackPressed,
        buttonNumber,
        bridgeDeviceType?.is_io_device,
        navigateToSTPEditor,
        goToStpEditorWithState,
    ]);

    const buttons = useMemo(() => [
        {
            text: 'SANAL KONTROL SİL',
            className: 'mulberry-wood',
            onClick: onDeletePressed,
            hidden: !(selected) || location.state,
        },
        {
            text: 'KAYDET',
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: 'İPTAL',
            className: 'red',
            url: BACK_URL,
        },
    ], [onCreatePressed, onDeletePressed, selected, location.state]);

    useEffect(() => {
        setControlBridgeDevice(controlBridgeDeviceOptionList.find(option => option.value.id === controlBridgeDevice?.value?.id) || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlBridgeDeviceOptionList]);

    useEffect(() => {
        setControl(controlOptionList.find(option => option.value.id === control?.value?.id) || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlOptionList]);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className='service-edit'
                backUrl={BACK_URL}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        {
                            renderIf(slot)(() =>
                                <>
                                    <SettingReadOnlyRowComponent
                                        title='Slot'
                                        value={slot}/>
                                </>,
                            )
                        }
                        <SettingSelectComponent
                            label='Cihaz'
                            options={bridgeDeviceOptionList}
                            onChange={setBridgeDevice}
                            value={bridgeDevice}/>
                        <SettingSelectComponent
                            label='Kontrol Edilecek Cihaz'
                            options={controlBridgeDeviceOptionList}
                            onChange={setControlBridgeDevice}
                            value={controlBridgeDevice}/>
                        <SettingSelectComponent
                            label='Kontrol'
                            options={controlOptionList}
                            onChange={setControl}
                            value={control}/>
                    </div>
                    <div className='content-wrapper'>
                        {
                            renderIf(bridgeDeviceType?.button_count > 0)(() =>
                                <SettingSelectComponent
                                    label='Buton Numarası'
                                    options={buttonNumberOptionList}
                                    onChange={setButtonNumber}
                                    value={buttonNumber}/>,
                            )
                        }
                        {
                            renderIf(selected?.id == null &&
                                bridgeDeviceType?.page_count > 0)(() =>
                                <SettingCheckBoxComponent
                                    label={bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_STS_PLUS ? 'STS PLUS Editörüne Git' : 'STP Editörüne Git'}
                                    set={setNavigateStpEditorCheckbox}
                                    value={navigateToSTPEditor}/>,
                            )
                        }
                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
